import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Mail, Building2, MessageSquare, Sparkles, Send, RefreshCw, CheckCircle2, Users } from "lucide-react";
import ApexLoader from "@/components/ApexLoader";

const STATUS_STYLES = {
  new: { label: "NEW", cls: "bg-[#dc2626]/15 text-[#fca5a5] border-[#dc2626]/40" },
  drafted: { label: "DRAFTED", cls: "bg-white/5 text-zinc-200 border-white/10" },
  replied: { label: "REPLIED", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
  reply_failed: { label: "FAILED", cls: "bg-yellow-500/10 text-yellow-300 border-yellow-500/30" },
};

function MessageCard({ item, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [reply, setReply] = useState(item.drafted_reply || "");
  const [subject, setSubject] = useState(`Re: your briefing request to Apex Infinity Ventures`);
  const [working, setWorking] = useState(null); // 'regen' | 'send'
  const [error, setError] = useState("");

  useEffect(() => {
    setReply(item.drafted_reply || "");
  }, [item.drafted_reply]);

  const regen = async () => {
    setWorking("regen");
    setError("");
    try {
      const { data } = await api.post(`/contact/${item.id}/regenerate`);
      setReply(data.drafted_reply);
      onUpdate({ ...item, drafted_reply: data.drafted_reply, status: "drafted" });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setWorking(null);
    }
  };

  const send = async () => {
    setWorking("send");
    setError("");
    try {
      const { data } = await api.post(`/contact/${item.id}/reply`, { body: reply, subject });
      const mode = data.send_result?.mode;
      onUpdate({
        ...item,
        drafted_reply: reply,
        status: data.send_result?.sent || mode === "console" ? "replied" : "reply_failed",
        reply_sent_at: new Date().toISOString(),
        reply_mode: mode,
      });
      setEditing(false);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setWorking(null);
    }
  };

  const status = STATUS_STYLES[item.status] || STATUS_STYLES.new;

  return (
    <div className="bg-[#121214] border border-white/5 p-6" data-testid="message-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="font-display text-2xl text-white leading-tight">{item.name}</div>
          <div className="font-mono-apex text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
            {new Date(item.created_at).toLocaleString()}
          </div>
        </div>
        <span className={`font-mono-apex text-[9px] uppercase tracking-[0.25em] px-2 py-1 border ${status.cls}`}>
          {status.label}
        </span>
      </div>

      <div className="grid sm:grid-cols-2 gap-2 mb-4 font-mono-apex text-xs text-zinc-400">
        <div className="flex items-center gap-2 truncate"><Mail size={12} /> {item.email}</div>
        {item.company && <div className="flex items-center gap-2 truncate"><Building2 size={12} /> {item.company}</div>}
        {item.company_size && <div className="flex items-center gap-2"><Users size={12} /> {item.company_size}</div>}
        {item.intent && <div className="flex items-center gap-2 truncate"><Sparkles size={12} /> {item.intent}</div>}
      </div>

      <div className="border-t border-white/5 pt-4 mb-4">
        <div className="font-mono-apex text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-2">their message</div>
        <div className="flex gap-2">
          <MessageSquare size={14} className="text-zinc-500 mt-0.5 shrink-0" />
          <p className="font-body text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">{item.message}</p>
        </div>
      </div>

      <div className="border-t border-white/5 pt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="font-mono-apex text-[9px] uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-2">
            <Sparkles size={11} className="text-[#dc2626]" /> sentinel-drafted reply
          </div>
          {item.status !== "replied" && (
            <button
              onClick={regen}
              disabled={working !== null}
              className="font-mono-apex text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-white flex items-center gap-1.5 disabled:opacity-50"
              data-testid="regen-draft-btn"
            >
              <RefreshCw size={11} className={working === "regen" ? "animate-spin" : ""} />
              {working === "regen" ? "regenerating…" : "regenerate"}
            </button>
          )}
        </div>

        {item.status === "replied" ? (
          <div className="bg-[#0a0a0b] border-l-2 border-emerald-500/60 p-4">
            <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-emerald-400 flex items-center gap-2 mb-2">
              <CheckCircle2 size={12} /> sent {new Date(item.reply_sent_at).toLocaleString()}
              {item.reply_mode === "console" && <span className="text-yellow-300 ml-2">· (console-mode — configure SMTP to send real emails)</span>}
            </div>
            <p className="font-body text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{item.drafted_reply}</p>
          </div>
        ) : editing ? (
          <div className="space-y-3">
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="input-apex w-full px-3 py-2 font-body text-sm"
              placeholder="Subject"
              data-testid="reply-subject-input"
            />
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={10}
              className="input-apex w-full px-3 py-3 font-body text-sm resize-y"
              data-testid="reply-body-input"
            />
            {error && <div className="font-mono-apex text-xs text-[#dc2626]">{error}</div>}
            <div className="flex gap-2">
              <button
                onClick={send}
                disabled={working !== null || !reply.trim()}
                className="btn-primary-apex px-5 py-2.5 font-mono-apex text-[10px] uppercase tracking-[0.25em] flex items-center gap-2 disabled:opacity-50"
                data-testid="send-reply-btn"
              >
                <Send size={12} /> {working === "send" ? "sending…" : "approve & send"}
              </button>
              <button
                onClick={() => { setEditing(false); setReply(item.drafted_reply || ""); }}
                disabled={working !== null}
                className="btn-secondary-apex px-5 py-2.5 font-mono-apex text-[10px] uppercase tracking-[0.25em]"
              >
                cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            {reply ? (
              <div className="bg-[#0a0a0b] border-l-2 border-[#dc2626]/60 p-4 mb-3">
                <p className="font-body text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{reply}</p>
              </div>
            ) : (
              <div className="font-mono-apex text-xs text-zinc-600 mb-3">No draft yet — regenerate to create one.</div>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => setEditing(true)}
                disabled={!reply}
                className="btn-primary-apex px-5 py-2.5 font-mono-apex text-[10px] uppercase tracking-[0.25em] flex items-center gap-2 disabled:opacity-50"
                data-testid="edit-reply-btn"
              >
                <Send size={12} /> review & send
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function DashboardMessages() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/contact/list");
        setItems(data);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const updateItem = (next) => {
    setItems((all) => all.map((it) => (it.id === next.id ? { ...it, ...next } : it)));
  };

  const stats = {
    total: items.length,
    new: items.filter((i) => i.status === "new").length,
    drafted: items.filter((i) => i.status === "drafted").length,
    replied: items.filter((i) => i.status === "replied").length,
  };

  return (
    <div className="space-y-6" data-testid="messages-page">
      <div>
        <div className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">admin · inbound</div>
        <h1 className="font-display text-4xl text-white tracking-tight">
          <em className="silver-text not-italic">Briefing</em> requests.
        </h1>
        <p className="font-body text-sm text-zinc-400 mt-2">
          Sentinel auto-drafts a reply for every inbound. Review, edit, and approve to send.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "TOTAL", value: stats.total },
          { label: "NEW", value: stats.new },
          { label: "DRAFTED", value: stats.drafted },
          { label: "REPLIED", value: stats.replied },
        ].map((s) => (
          <div key={s.label} className="bg-[#121214] border border-white/5 p-4">
            <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">{s.label}</div>
            <div className="font-mono-apex text-2xl text-white">{s.value}</div>
          </div>
        ))}
      </div>

      {loading && (
        <div className="flex items-center gap-3 py-6" data-testid="dash-messages-loading">
          <ApexLoader inline size={18} />
          <span className="font-mono-apex text-xs uppercase tracking-[0.3em] text-zinc-500">loading</span>
        </div>
      )}
      {error && <div className="font-mono-apex text-xs text-[#dc2626]">{error}</div>}

      <div className="space-y-4">
        {items.map((it) => (
          <MessageCard key={it.id} item={it} onUpdate={updateItem} />
        ))}
        {!loading && items.length === 0 && (
          <div className="font-mono-apex text-xs text-zinc-600">No transmissions yet.</div>
        )}
      </div>
    </div>
  );
}
